//@ts-check
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import { useCallback, useMemo, useRef, useState } from "react";
import ArcGISMap from "../../maps/components/ArcGISMap/ArcGISMap";

import { GraphicManipulator } from "@arcgis/core/views/interactive/GraphicManipulator";
import Extent from "@arcgis/core/geometry/Extent";
import Point from "@arcgis/core/geometry/Point";
import { pt2px, px2pt } from "@arcgis/core/core/screenUtils";
import Graphic from "@arcgis/core/Graphic";
import { addInterceptors} from "../../maps/addInterceptors";

const { REACT_APP_IMAGEPATH } = process.env;
const pin = `${REACT_APP_IMAGEPATH}/map-pin.svg`;

GraphicManipulator.prototype._intersectDistance2D = (function (existingMethod) {
  // HACK: This method is supposed to determine if a screen point intersects
  // a symbol. However, for picture marker symbols, it does not work correctly.
  // So this monkeypatches the method to make it work by computing the extent
  // of the picture marker symbol by converting size/offsets (pixel/point units) into an
  // Extent measured in map units.
  return function PATCH_intersectDistance2D(...args) {
    const [view, screenPoint, geometry, symbol] = args;
    if (geometry.type === "point" && symbol.type === "picture-marker") {
      const mapPoint = view.toMap(screenPoint);

      const markerWidthInMapUnits = pt2px(symbol.width) * view.resolution;
      const markerHeightInMapUnits = pt2px(symbol.height) * view.resolution;
      const markerXoffsetInMapUnits = pt2px(symbol.xoffset) * view.resolution;
      const markerYoffsetInMapUnits = pt2px(symbol.yoffset) * view.resolution;

      const pictureMarkerExtent = new Extent({
        xmin: geometry.x - markerWidthInMapUnits / 2 + markerXoffsetInMapUnits,
        ymin: geometry.y - markerHeightInMapUnits / 2 + markerYoffsetInMapUnits,
        xmax: geometry.x + markerWidthInMapUnits / 2 + markerXoffsetInMapUnits,
        ymax: geometry.y + markerHeightInMapUnits / 2 + markerYoffsetInMapUnits,
        spatialReference: view.spatialReference,
      });

      return pictureMarkerExtent.contains(mapPoint) ? 1 : null;
    }
    return existingMethod.apply(this, args);
  };
})(GraphicManipulator.prototype._intersectDistance2D);

const pinSymbol = new PictureMarkerSymbol({
  height: px2pt(35),
  width: px2pt(22),
  yoffset: px2pt(32 / 2),
  url: pin,
});

export const ContributionMap = (props) => {
  const { easting, northing } = props;
  addInterceptors();
  // Map props
  // useMemo is used to avoid creating new objects on each re-render
  // causing the map to be torn down and created again on each re-render
  const containerProperties = useMemo(
    () => ({
      className: "ugc__map ugc__map--contribution",
    }),
    []
  );
  const viewProperties = useMemo(() => {
    /** @type {__esri.MapViewProperties} */
    const props = {
      center: {
        // This form of object creating is known as "autocasting", which avoids the
        // need to import dependencies, but can sometimes confuse IntelliSense (not true here)
        // https://developers.arcgis.com/javascript/latest/programming-patterns/#autocasting
        type: "point",
        x: parseFloat(easting),
        y: parseFloat(northing),
        spatialReference: { wkid: 27700 },
      },
      constraints: {
        geometry: new Extent({
          xmin: parseFloat(easting) - 1,
          ymin: parseFloat(northing) - 1,
          xmax: parseFloat(easting) + 1,
          ymax: parseFloat(northing) + 1,
          spatialReference: { wkid: 27700 },
        }),
        minZoom: 14,
        maxZoom: 18,
        rotationEnabled: false, // Disables map rotation
      },
      zoom: 14,
      spatialReference: { wkid: 27700 },
      ui: {
        // This controls the padding between the map's UI controls and the container.
        // Try not to use CSS for this as ArcGIS's popup positioning logic only respects the
        // value set here, not your CSS.
        padding: 10,
      },
    };
    return props;
  }, [easting, northing]);

  // This will be the layer that contains the pin that the user is interacting with.
  // A GraphicsLayer can have any kinds of geometry inside of it at any time, but
  // has less features when compared to a FeatureLayer (e.g. querying for geometries in a certain area must be done manually)

  const point = new Point({
    x: easting,
    y: northing,
    spatialReference: { wkid: 27700 },
  });
  const centrePinGraphic = new Graphic({
    geometry: point,
    symbol: pinSymbol,
  });

  const userPinLayer = useMemo(() => new GraphicsLayer(), []);
  userPinLayer.add(centrePinGraphic);

  const mapProperties = useMemo(() => {
    /** @type {__esri.MapProperties} */
    const props = {
      basemap: {
        baseLayers: [
          new VectorTileLayer({
            url:
              "https://api.os.uk/maps/vector/v1/vts",
            style: require("../../maps/OS_VTS_27700_ESRI.json"),
          }),
        ],
      },
      layers: [userPinLayer],
    };
    return props;
  }, [userPinLayer]);

  // Using refs as events coming out of ArcGIS are not tied to the
  // render cycle of React, so using useState just results in unnecessary
  // re-renders (negligable I know) and annoying "missing dependency" ESLint errors
  const sketchViewModelRef = useRef(
    /** @type {__esri.SketchViewModel | undefined} */ (undefined)
  );
  const mapViewRef = useRef(
    /** @type {__esri.MapView | undefined} */ (undefined)
  );

  const onMapInitialized = useCallback(
    async (
      /** @type {__esri.Map} */ map,
      /** @type {__esri.MapView} */ mapView
    ) => {
      mapViewRef.current = mapView;
    },
    [userPinLayer]
  );

  return (
    <ArcGISMap
      view="2d"
      containerProperties={containerProperties}
      mapProperties={mapProperties}
      viewProperties={viewProperties}
      onMapInitialized={onMapInitialized}
    ></ArcGISMap>
  );
};
